import React, { useState, useEffect } from 'react';
import { Camera, Edit, X } from "lucide-react";
import { useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Loading from '../../component/Loading/Loading';
import AlertMsg from '../../component/AlertMsg/AlertMsg';
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";
import useAxiosInstance from '../../services/useAxiosInstance';

function MemberProfile() {
  const axiosInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);
  const userId = useSelector((state) => state.auth.userId);
  const currentDate = new Date();
  const formattedDates = currentDate.toISOString();
  const [user, setUser] = useState(null);
  const communityId = useSelector((state) => state.auth.communityId);
  const clientId = useSelector((state) => state.auth.clientId);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    emailId: '',
    mobileNumber: '',
    address: '',
    city: '',
    stateId: '',
    zipCode: '',
    addressId: 0,
    countryId: '',
    userId: userId,
    middleName: "",
    mobileIsVerified: true,
    emailIdIsVerified: true,
    isClient: true,
    otp: 0,
    roleId: 0,
    password: "",
    activeStatus: true,
    createdDate: formattedDates,
    modifiedById: userId,
    modifiedDate: formattedDates,
    lastLogin: null,
    userProfileUrl: '',
  });

  const [countryData, setCountryData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [isProfileImageEdit, setIsProfileImageEdit] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
  const [addressIdss, setAddressIdss] = useState(null);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isAddressEditing, setIsAddressEditing] = useState(false);

  const { addressId } = useParams();

  const [address, setAddress] = useState({
    address: '',
    city: '',
    stateId: '',
    countryId: '',
    zipCode: ''
  });

  const validateZipCode = (zipCode) => {
    const zipCodePattern = /^\d{5}$/;
    return zipCodePattern.test(zipCode);
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/user/getById/${userId}`);
      setUser(response.data);

      setFormData((prevFormData) => ({
        ...prevFormData,
        firstName: response.data.firstName || '',
        lastName: response.data.lastName || '',
        emailId: response.data.emailId || '',
        mobileNumber: response.data.mobileNumber || '',
        userId: response.data.userId || '',
        middleName: response.data.middleName || '',
        mobileIsVerified: response.data.mobileIsVerified || '',
        emailIdIsVerified: response.data.emailIdIsVerified || '',
        isClient: response.data.isClient || '',
        otp: response.data.otp || '',
        roleId: response.data.roleId || '',
        password: response.data.password || '',
        activeStatus: response.data.activeStatus || '',
        createdDate: response.data.createdDate || '',
        modifiedById: response.data.modifiedById || '',
        modifiedDate: response.data.modifiedDate || '',
        lastLogin: response.data.lastLogin || '',
        userProfileUrl: response.data.userProfileUrl || '',
      }));

      const clientAddressResponse = await axiosInstance.get(`/api/public/clientaddress/getByClientId/${clientId}`);
      const clientAddressData = clientAddressResponse.data;

      if (Array.isArray(clientAddressData) && clientAddressData.length > 0) {
        setAddressIdss(clientAddressData[0].addressid);
      }
    } catch (error) {
      console.error('Error fetching user or address:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [userId, clientId]);

  useEffect(() => {
    const fetchAddressData = async () => {
      setLoading(true);
      try {
        const addressResponse = await axiosInstance.get(`/api/public/address/getOne/${addressIdss}`);
        const addressData = addressResponse.data;

        setFormData((prevFormData) => ({
          ...prevFormData,
          address: addressData.address || '',
          city: addressData.city || '',
          stateId: addressData.stateId || '',
          zipCode: addressData.zipCode || '',
          countryId: addressData.countryId || '',
          addressId: addressData.addressId || '',
          activeStatus: addressData.activeStatus || ''
        }));
        setSelectedCountryId(addressData.countryId || '');

      } catch (error) {
        console.error('Error fetching address data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (addressIdss) {
      fetchAddressData();
    }
  }, [addressIdss]);

  useEffect(() => {
    axiosInstance.get(`/api/public/country/getAll`)
      .then(response => setCountryData(response.data))
      .catch(error => console.error('Error fetching countries:', error));
  }, []);

  useEffect(() => {
    axiosInstance.get(`/api/public/state/getAll`)
      .then(response => setStateData(response.data))
      .catch(error => console.error('Error fetching states:', error));
  }, []);

  const handleCancel = () => {
    setIsEditing(false);
    setIsProfileImageEdit(false);
    setFormErrors({});
    fetchUser();
  };

  const handleEdit = () => setIsEditing(true);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    if (name === 'countryId') {
      setSelectedCountryId(value);
    }
  };

  const handlePhoneChange = (value, country, e, formattedValue) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      mobileNumber: value,
    }));
  };

  const validateForm = () => {
    let errors = {};

    // Required field validation
    if (!formData.firstName.trim()) errors.firstName = "First Name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last Name is required";
    if (!formData.emailId.trim()) errors.emailId = "Email is required";
    if (!formData.mobileNumber.trim()) errors.mobileNumber = "Phone Number is required";
    if (!formData.city.trim()) errors.city = "City is required";
    if (!formData.address.trim()) errors.address = "Address is required";
    if (!formData.stateId) errors.stateId = "State is required";
    if (!formData.countryId) errors.countryId = "Country is required";
    if (!formData.zipCode) errors.zipCode = "Zip Code is required";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.emailId && !emailRegex.test(formData.emailId)) {
      errors.emailId = "Enter a valid email address";
    }

    // Phone number validation (Ensure it has at least 10 digits)
    if (formData.mobileNumber && formData.mobileNumber.replace(/\D/g, "").length < 10) {
      errors.mobileNumber = "Enter a valid phone number";
    }

    setFormErrors(errors);
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      return; // Exit if there are errors
    }

    try {
      setLoading(true);
      await axiosInstance.put(
        `/api/user/update${userId}`,
        {
          activeStatus: formData.activeStatus,
          createdDate: formData.createdDate,
          firstName: formData.firstName,
          lastName: formData.lastName,
          emailId: formData.emailId,
          mobileNumber: formData.mobileNumber,
          emailIdIsVerified: formData.emailIdIsVerified,
          isClient: formData.isClient,
          lastLogin: formData.lastLogin,
          middleName: formData.middleName,
          mobileIsVerified: formData.mobileIsVerified,
          modifiedById: userId,
          modifiedDate: new Date().toISOString(),
          otp: formData.otp,
          password: formData.password,
          roleId: formData.roleId,
          userId: userId,
          userProfileUrl: formData.userProfileUrl,
        });

      await axiosInstance.put(
        `/api/public/address/update/${formData.addressId}`,
        {
          activeStatus: formData.activeStatus,
          address: formData.address,
          city: formData.city,
          stateId: formData.stateId,
          zipCode: formData.zipCode,
          countryId: formData.countryId,
          addressId: formData.addressId,
        });
      setIsEditing(false);
      setAlert({ visible: true, message: 'Profile updated successfully!', type: 'success' });
    } catch (error) {
      console.error('Error updating user or address:', error);
      setAlert({ visible: true, message: 'Failed to update the profile. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => setIsProfileImageEdit(true);

  const getCountryName = (countryId) => {
    const country = countryData.find(country => country.countryid === parseInt(countryId));
    return country ? country.countryname : '';
  };

  const getStateName = (stateId) => {
    const state = stateData.find(state => state.stateid === parseInt(stateId));
    return state ? state.statename : '';
  };

  const handleProfileImageClick = () => {
    document.getElementById('profileImageInput').click();
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLoading(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      try {
        const uploadResponse = await axiosInstance.post('/api/user/userProfile', uploadFormData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        const newDocUrl = uploadResponse.data;

        try {
          await axiosInstance.patch(`/api/user/updateUserProfile/${userId}`, null, {
            params: {
              userProfileUrl: newDocUrl,
              userId: userId,
            }
          });

          setFormData((prevData) => ({
            ...prevData,
            userProfileUrl: newDocUrl,
          }));
          setIsProfileImageEdit(false);
          setAlert({
            visible: true,
            message: 'Profile image updated successfully!',
            type: 'success',
          });
        } catch (error) {
          console.error('Error updating profile image URL', error);
          setAlert({
            visible: true,
            message: 'Failed to update the profile image.',
            type: 'error',
          });
        }
      } catch (error) {
        console.error('Error uploading file', error);
        setAlert({
          visible: true,
          message: 'Failed to upload the profile image.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    } else {
      console.error('No file selected or file input is not properly defined.');
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddress(prevAddress => ({ ...prevAddress, [name]: value }));
  };

  const validateFormAddress = () => {
    let errors = {};
    if (!address.address) errors.address = "Address is required";
    if (!address.city) errors.city = "City is required";
    if (!address.zipCode) errors.zipCode = "ZIP Code is required";
    if (address.zipCode && !validateZipCode(address.zipCode)) errors.zipCode = "Invalid ZIP Code";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddressSubmit = (e) => {
    e.preventDefault();

    if (!validateFormAddress()) {
      return;
    }

    const url = isEditing
      ? `/api/public/address/update/${addressId}`
      : `/api/public/address/add`;

    const method = isEditing ? 'put' : 'post';

    axiosInstance({
      method,
      url,
      data: address
    })
      .then(response => {
        console.log('Address submitted successfully:', response.data);

        if (!isEditing) {
          const { addressId } = response.data;

          const clientAddressData = {
            activestatus: true,
            addressid: addressId,
            clientaddressid: 0,
            clientid: clientId,
            isrented: true,
            tenantid: null
          };

          return axiosInstance.post(`/api/public/clientaddress/add`, clientAddressData);
        }
      })
      .then(response => {
        if (!isEditing) {
          console.log('Client address submitted successfully:', response.data);
        }
        setIsAddressModalOpen(false);
        setAlert({ visible: true, message: 'Address updated successfully!', type: 'success' });
      })
      .catch(error => {
        console.error('Error submitting address:', error);
        setAlert({ visible: true, message: 'Failed to update address. Please try again.', type: 'error' });
      });
  };

  const openAddressModal = () => setIsAddressModalOpen(true);

  const resetFormData = () => {
    setAddress({
      address: '',
      city: '',
      stateId: '',
      countryId: '',
      zipCode: ''
    });
  };

  const closeAddressModal = () => {
    resetFormData();
    setFormErrors({});
    setIsAddressModalOpen(false);
  };

  // Modal footer with action buttons
  const addressModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={closeAddressModal}
        type="button"
      >
        Cancel
      </button>
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={handleAddressSubmit}
      >
        {isAddressEditing ? 'Update Address' : 'Add Address'}
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}

          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Profile Information</h1>
            <div className="flex space-x-2">
              {isEditing ? (
                <>
                  <button
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    onClick={handleSubmit}
                  >
                    Save
                  </button>
                </>
              ) : (
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
                  onClick={handleEdit}
                >
                  <Edit size={18} />
                  <span>Edit Profile</span>
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Profile Image Section */}
                <div className="w-full lg:w-1/4 flex flex-col items-center">
                  <div className="relative">
                    <div className="w-36 h-36 rounded-full overflow-hidden mb-4 border-4 border-gray-200">
                      <img
                        src={formData.userProfileUrl || require("../../assets/profile.png")}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {isProfileImageEdit ? (
                      <>
                        <input
                          type="file"
                          id="profileImageInput"
                          onChange={handleProfileImageChange}
                          className="hidden"
                        />
                        <button
                          onClick={handleProfileImageClick}
                          className="absolute bottom-4 right-0 bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600"
                          title="Choose a photo"
                        >
                          <Camera size={18} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleEditClick}
                        className="absolute bottom-4 right-0 bg-gray-100 text-gray-600 p-2 rounded-full hover:bg-gray-200"
                        title="Edit profile photo"
                      >
                        <Edit size={18} />
                      </button>
                    )}
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mt-2">
                    {formData.firstName} {formData.lastName}
                  </h3>
                  <p className="text-sm text-gray-500">{formData.emailId}</p>
                </div>

                {/* Form Fields Section */}
                <div className="w-full lg:w-3/4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={100}
                        name="firstName"
                        value={formData.firstName}
                        readOnly={!isEditing}
                        onChange={handleChange}
                        className={`w-full rounded-md border ${formErrors.firstName ? "border-red-500" : "border-gray-300"} px-4 py-2 ${!isEditing ? "bg-gray-50" : "focus:outline-none focus:ring-2 focus:ring-blue-500"}`}
                      />
                      {formErrors.firstName && <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={100}
                        name="lastName"
                        value={formData.lastName}
                        readOnly={!isEditing}
                        onChange={handleChange}
                        className={`w-full rounded-md border ${formErrors.lastName ? "border-red-500" : "border-gray-300"} px-4 py-2 ${!isEditing ? "bg-gray-50" : "focus:outline-none focus:ring-2 focus:ring-blue-500"}`}
                      />
                      {formErrors.lastName && <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        maxLength={100}
                        name="emailId"
                        value={formData.emailId}
                        readOnly={!isEditing}
                        onChange={handleChange}
                        className={`w-full rounded-md border ${formErrors.emailId ? "border-red-500" : "border-gray-300"} px-4 py-2 ${!isEditing ? "bg-gray-50" : "focus:outline-none focus:ring-2 focus:ring-blue-500"}`}
                      />
                      {formErrors.emailId && <p className="text-red-500 text-xs mt-1">{formErrors.emailId}</p>}
                    </div>

                    <div>
                      <label className="block  text-sm font-medium text-gray-700 mb-1">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <PhoneInput
                        inputProps={{
                          name: 'mobileNumber',
                          required: true,
                          className: `w-full rounded-md border  ${formErrors.mobileNumber ? "border-red-500" : "border-gray-300"} px-4 py-2 pl-12 ${!isEditing ? "bg-gray-50" : "focus:outline-none focus:ring-2 focus:ring-blue-500"}`
                        }}
                        value={formData.mobileNumber}
                        onChange={handlePhoneChange}
                        maxLength={100}
                        country={'us'}
                        disabled={!isEditing}
                        containerClass="w-full"
                        buttonStyle={{
                          border: "none",
                          background: !isEditing ? "transparent" : "transparent"
                        }}
                      />
                      {formErrors.mobileNumber && <p className="text-red-500 text-xs mt-1">{formErrors.mobileNumber}</p>}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={100}
                        name="address"
                        value={formData.address}
                        readOnly={!isEditing}
                        onChange={handleChange}
                        className={`w-full rounded-md border ${formErrors.address ? "border-red-500" : "border-gray-300"} px-4 py-2 ${!isEditing ? "bg-gray-50" : "focus:outline-none focus:ring-2 focus:ring-blue-500"}`}
                      />
                      {formErrors.address && <p className="text-red-500 text-xs mt-1">{formErrors.address}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={100}
                        name="city"
                        value={formData.city}
                        readOnly={!isEditing}
                        onChange={handleChange}
                        className={`w-full rounded-md border ${formErrors.city ? "border-red-500" : "border-gray-300"} px-4 py-2 ${!isEditing ? "bg-gray-50" : "focus:outline-none focus:ring-2 focus:ring-blue-500"}`}
                      />
                      {formErrors.city && <p className="text-red-500 text-xs mt-1">{formErrors.city}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Zip Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={50}
                        name="zipCode"
                        value={formData.zipCode}
                        readOnly={!isEditing}
                        onChange={handleChange}
                        className={`w-full rounded-md border ${formErrors.zipCode ? "border-red-500" : "border-gray-300"} px-4 py-2 ${!isEditing ? "bg-gray-50" : "focus:outline-none focus:ring-2 focus:ring-blue-500"}`}
                      />
                      {formErrors.zipCode && <p className="text-red-500 text-xs mt-1">{formErrors.zipCode}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country <span className="text-red-500">*</span>
                      </label>
                      {isEditing ? (
                        <select
                          name="countryId"
                          value={formData.countryId}
                          onChange={handleChange}
                          className={`w-full rounded-md border ${formErrors.countryId ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        >
                          <option value="">Select Country</option>
                          {countryData.map(country => (
                            <option key={country.countryid} value={country.countryid}>{country.countryname}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={getCountryName(formData.countryId)}
                          readOnly
                          className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                        />
                      )}
                      {formErrors.countryId && <p className="text-red-500 text-xs mt-1">{formErrors.countryId}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State <span className="text-red-500">*</span>
                      </label>
                      {isEditing ? (
                        <select
                          name="stateId"
                          value={formData.stateId}
                          onChange={handleChange}
                          className={`w-full rounded-md border ${formErrors.stateId ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        >
                          <option value="">Select State</option>
                          {stateData
                            .filter(state => state.country?.countryid === parseInt(selectedCountryId))
                            .map(state => (
                              <option key={state.stateid} value={state.stateid}>{state.statename}</option>
                            ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={getStateName(formData.stateId)}
                          readOnly
                          className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                        />
                      )}
                      {formErrors.stateId && <p className="text-red-500 text-xs mt-1">{formErrors.stateId}</p>}
                    </div>
                  </div>

                  <div className="mt-4 text-sm text-red-500">
                    * Required fields
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Address Modal */}
          <GlassmorphicModal
            isOpen={isAddressModalOpen}
            onClose={closeAddressModal}
            title="Address Information"
            size="md"
            footer={addressModalFooter}
          >
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={address.address}
                  onChange={handleAddressChange}
                  className={`w-full rounded-md border ${formErrors.address ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {formErrors.address && <p className="text-red-500 text-xs mt-1">{formErrors.address}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={address.city}
                  onChange={handleAddressChange}
                  className={`w-full rounded-md border ${formErrors.city ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {formErrors.city && <p className="text-red-500 text-xs mt-1">{formErrors.city}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="zipCode"
                  value={address.zipCode}
                  onChange={handleAddressChange}
                  className={`w-full rounded-md border ${formErrors.zipCode ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {formErrors.zipCode && <p className="text-red-500 text-xs mt-1">{formErrors.zipCode}</p>}
              </div>

              <div className="text-sm text-red-500">
                * Required fields
              </div>
            </div>
          </GlassmorphicModal>
        </div>
      )}
    </div>
  );
}

export default MemberProfile;